import axios, { AxiosRequestHeaders } from "axios";
import { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { getAuth } from "../../../../app/modules/auth";
import { KTSVG } from "../../../helpers";
import { toastfy } from "../../toast/Toast";
import { showErrorMessage } from "../../../helpers/MessagesHelper";
import { useNavigate } from "react-router-dom";
import { useStyleContext } from "../../../helpers/StyleContext";
import clsx from "clsx";
import { InputFile } from "../../form/components/fields/InputFile";

export interface modalSettingsInterface {
  itemId: number;
  deleteItemDescriptionOrName?: string;
  module: string;
  toBan?: boolean;
}

type SignedContractModalProps = {
  modalSettings: modalSettingsInterface;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleClose: () => void;
  loadList: (module: string, headers: AxiosRequestHeaders) => Promise<void>;
  setDashboardUpdate?: React.Dispatch<React.SetStateAction<boolean>>;
};

const SignedContractModal: React.FC<SignedContractModalProps> = ({
  modalSettings,
  showModal,
  setShowModal,
  handleClose,
  loadList,
  setDashboardUpdate,
}) => {
  const navigate = useNavigate();
  const [disabledSignedContractButton, setDisabledSignedContractButton] =
    useState(false);
  const { styleState } = useStyleContext();
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiToken = getAuth();
  const token = apiToken?.api_token;
  const [selectedDoc, setSelectedDoc] = useState<File | null>(null);

  const handleSelectDoc = (doc: any) => {
    setSelectedDoc(doc);
  };

  const formData = new FormData();

  if (selectedDoc) {
    formData.append("file", selectedDoc);
  }

  const handleSignedContract = (module: string, id: Number) => {
    setDisabledSignedContractButton(true);

    const headers = {
      Authorization: `Bearer ${token}`,
      operation: `${module}/update/signed`,
    };

    axios
      .put(`${apiUrl}/${module}/${id}/update/signed`, formData, { headers })
      .then((response) => {
        setShowModal(false);

        setTimeout(() => {
          setDisabledSignedContractButton(false);
        }, 1000);

        loadList(module, headers);

        if (setDashboardUpdate) {
          setDashboardUpdate(true);
        }

        toastfy("success", "Operação bem sucedida! O modelo de contrato foi atualizado.");
      })
      .catch((error) => {
        setTimeout(() => {
          setDisabledSignedContractButton(false);
        }, 1000);

        showErrorMessage(error?.response?.status, error?.response?.data?.error);

        if (error?.response?.status === 403) {
          navigate("/");
        }
      });
  };

  return (
    <Modal show={showModal} onHide={handleClose} centered={true}>
      <style>
        {`
          .btn-close {
            position: absolute;
            right: 4%;
            top: 7%;
          }
          .modal-content {
            background-color: ${styleState?.secondary_color}
          }
        `}
      </style>
      <Modal.Header style={{ textAlign: "center" }} closeButton>
        <Modal.Title
          style={{ width: "100%", color: styleState.secondary_text_color }}
        >
          <KTSVG
            path="/media/icons/duotune/general/gen044.svg"
            className="svg-icon-5 svg-icon-5x svg-icon-warning"
          />
          <br />
          Atenção!
          <br />
          Você deseja realizar o envio do contrato assinado?
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="notice d-flex bg-light-warning rounded border-warning border border-dashed p-6 mb-4">
          Ao continuar com a operação esse contrato assinado será vinculado no
          item, seu status será atualizado para "vigente" e as cobranças serão
          geradas conforme os parâmetros do plano (valor, duração) e dos
          parâmetros do contrato (data de início, pagamento à vista).
        </div>
        <table className="table align-middle gs-0 gy-4">
          <thead>
            <tr
              className={clsx("fw-bold", {
                "text-muted bg-light": !styleState.secondary_text_color,
              })}
              style={{
                backgroundColor: styleState?.secondary_text_color,
                color: styleState?.secondary_color,
              }}
            >
              <th
                key={0}
                className="ps-4 rounded-start"
                style={{ maxWidth: "30px" }}
              >
                #
              </th>
              <th key={1} className="ps-4 rounded-end">
                Descrição
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <span
                  className={clsx("fw-bold text-hover-secondary mb-1 fs-6", {
                    "text-dark": !styleState.secondary_text_color,
                  })}
                  style={{
                    color: styleState?.secondary_text_color,
                  }}
                >
                  {modalSettings.itemId}
                </span>
              </td>
              <td>
                <span
                  className={clsx("fw-bold text-hover-secondary mb-1 fs-6", {
                    "text-dark": !styleState.secondary_text_color,
                  })}
                  style={{
                    color: styleState?.secondary_text_color,
                  }}
                >
                  {modalSettings.deleteItemDescriptionOrName}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <div>
          <form encType="multipart/form-data">
            <div className="card-body border-top p-9">
              <div className="row mb-6">
                <label
                  className="col-lg-12 col-form-label required fw-semibold fs-6"
                  style={{
                    color: styleState?.secondary_text_color,
                  }}
                >
                  Upload do contrato assinado
                </label>
                <div className="col-lg-12 fv-row">
                  <InputFile
                    className="min-h-120px h-150px form-control form-control-lg text-center"
                    name="file"
                    onSelectImage={handleSelectDoc}
                  ></InputFile>
                </div>
              </div>
            </div>
          </form>
        </div>
        <span style={{ color: styleState.secondary_text_color }}>
          Para continuar, clique em enviar ou para cancelar a operação clique em
          cancelar.
        </span>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="info"
          onClick={(event) => {
            event.preventDefault();
            handleSignedContract(modalSettings.module, modalSettings.itemId);
          }}
          disabled={disabledSignedContractButton}
        >
          <span
            style={{ display: disabledSignedContractButton ? "none" : "block" }}
          >
            Enviar
          </span>
          <span
            className="indicator-progress"
            style={{ display: disabledSignedContractButton ? "block" : "none" }}
          >
            Enviando...
            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
          </span>
        </Button>
        <Button variant="secondary" onClick={handleClose}>
          Cancelar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SignedContractModal;
